import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';
import PublicAnalyticsBeacon from '../components/PublicAnalyticsBeacon';
import { FileDown, Lock, ShieldCheck, CheckCircle2, ArrowLeft, FileText } from 'lucide-react';

const GBP = (v) => new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(Number(v || 0));

/**
 * Extract the rendered body of a pasted HTML string.
 * Accepts either a full `<!DOCTYPE html><html><head>...</head><body>...</body></html>`
 * document or a bare fragment. When given a full document, we keep the entire
 * <head> + <body> content so the author's <style>, fonts, and layout all
 * survive — they are inserted directly into the page.
 */
function extractHtmlBody(html) {
    if (typeof html !== 'string') return '';
    const trimmed = html.trim();
    // Strip doctype only; keep everything else so pasted CSS/styles still apply.
    const noDoctype = trimmed.replace(/^<!doctype[^>]*>/i, '').trim();
    // If it's a full <html> document, splice <head> and <body> contents together.
    const headMatch = noDoctype.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
    const bodyMatch = noDoctype.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
        const headContent = headMatch ? headMatch[1] : '';
        return headContent + bodyMatch[1];
    }
    // Fragment — return as-is.
    return noDoctype;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public narrative-proposal viewer.
 *
 * Pulls the same data as /p/:token but renders the editable narrative blocks
 * defined in proposal.narrative. Brand colours, logo and company info come
 * from admin_settings; client name comes from the linked client. Heading
 * structure is fixed; every text/option/module is editable from the in-app
 * editor (ProposalDetail → Narrative tab).
 */
export default function PublicProposalNarrative() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const isPrint = searchParams.get('print') === '1';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const { data: d } = await axios.get(`${API}/public/proposals/${token}`);
                if (!live) return;
                setData(d);
            } catch (e) {
                if (!live) return;
                setErr(e?.response?.data?.detail || 'Could not load proposal.');
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, [token]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-400 text-sm">Loading…</div>;
    if (err) return <div className="min-h-screen flex items-center justify-center text-rose-500 text-sm">{err}</div>;
    if (!data?.proposal?.narrative) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-3">
                <p className="text-neutral-700 text-base">This proposal doesn't have a narrative version yet.</p>
                <Link to={`/p/${token}`} className="text-[#002FA7] text-sm underline">View standard proposal →</Link>
            </div>
        );
    }

    return <NarrativeView {...data} token={token} isPrint={isPrint} />;
}

/* ──────────────────────────────────────────────────────────────────────── */

function NarrativeView({ proposal, client, settings, token, isPrint }) {
    const n = proposal.narrative || {};
    const brand = settings?.brand_primary || '#0B3C5D';
    const accent = settings?.brand_accent || '#3BC6B4';
    const companyName = settings?.company_name || 'Beyond Measure';
    const logo = settings?.company_logo_url;
    const companyAddress = settings?.company_address || '';
    const clientName = client?.company_name || proposal.client_name || '—';

    // CSS variables to make brand colours theme the whole page.
    const themeStyle = { '--np-brand': brand, '--np-accent': accent };

    return (
        <div className={`np-root min-h-screen ${isPrint ? 'np-print' : ''}`} style={themeStyle} data-print={isPrint ? '1' : '0'}>
            <style>{NARRATIVE_CSS}</style>
            {!isPrint && <PublicAnalyticsBeacon token={token} viewKind="narrative" />}

            {/* Top bar — only render in structured mode (HTML pastes bring their own) */}
            {!n.html && (
                <header className="np-header">
                    <div className="np-header-inner">
                        <div className="np-brand-mark">
                            {logo
                                ? <img src={logo} alt={companyName} className="np-logo" />
                                : <span className="np-brand-name">{companyName}</span>}
                        </div>
                        <div className="np-header-meta">
                            <span className="np-meta-label">Prepared for</span>
                            <span className="np-meta-value" data-testid="np-client-name">{clientName}</span>
                        </div>
                    </div>
                </header>
            )}

            {/* Custom HTML mode — user pasted a full HTML document */}
            {n.html && (
                <div
                    className="np-html-wrap"
                    data-testid="np-html-content"
                    dangerouslySetInnerHTML={{ __html: extractHtmlBody(n.html) }}
                />
            )}

            {/* Structured narrative blocks (skip when user pasted HTML) */}
            {!n.html && n.cover && (
                <section className="np-section np-hero">
                    <div className="np-container">
                        <span className="np-eyebrow">{n.cover.eyebrow}</span>
                        <h1 className="np-headline" dangerouslySetInnerHTML={{ __html: n.cover.headline || '' }} />
                        <p className="np-sub">{n.cover.sub_headline}</p>
                        {Array.isArray(n.cover.meta) && n.cover.meta.length > 0 && (
                            <div className="np-meta-grid">
                                {n.cover.meta.map((m, i) => (
                                    <div key={i} className="np-meta-card">
                                        <span className="np-meta-card-label">{m.label}</span>
                                        <span className="np-meta-card-value">{m.value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {!n.html && n.understanding && (
                <Section title={n.understanding.title || 'What we heard'} lead={n.understanding.lead}>
                    <div className="np-grid-3">
                        {(n.understanding.items || []).map((it, i) => (
                            <div key={i} className="np-card">
                                {it.mark && <span className="np-card-mark">{it.mark}</span>}
                                <h3 className="np-card-title">{it.title}</h3>
                                <p className="np-card-body">{it.body}</p>
                            </div>
                        ))}
                    </div>
                    {n.understanding.pull_quote && (
                        <blockquote className="np-quote">"{n.understanding.pull_quote}"</blockquote>
                    )}
                </Section>
            )}

            {/* Options */}
            {!n.html && n.options_section && (
                <Section title={n.options_section.title || 'Routes forward'} lead={n.options_section.lead}>
                    <div className="np-grid-options">
                        {(n.options_section.options || []).map((o, i) => (
                            <article key={i} className={`np-option ${o.featured ? 'is-featured' : ''}`} data-testid={`np-option-${i}`}>
                                {o.badge && <span className="np-option-badge">{o.badge}</span>}
                                <span className="np-option-tag">{o.tag}</span>
                                <h3 className="np-option-name" dangerouslySetInnerHTML={{ __html: o.name || '' }} />
                                <p className="np-option-tagline">{o.tag_line}</p>
                                <div className="np-option-price-block">
                                    <span className="np-option-price">{o.price}</span>
                                    <span className="np-option-price-per">{o.price_per}</span>
                                    {o.price_sub && <span className="np-option-price-sub">{o.price_sub}</span>}
                                </div>
                                {o.monthly && (
                                    <div className="np-option-monthly">
                                        <span className="np-option-monthly-amount">{o.monthly}</span>
                                        <span className="np-option-monthly-sub">{o.monthly_sub}</span>
                                    </div>
                                )}
                                {Array.isArray(o.features) && o.features.length > 0 && (
                                    <ul className="np-option-features">
                                        {o.features.map((f, j) => <li key={j}>{f}</li>)}
                                    </ul>
                                )}
                                {o.timeline_label && (
                                    <p className="np-option-timeline">{o.timeline_label}</p>
                                )}
                                {o.footer && <p className="np-option-footer">{o.footer}</p>}
                            </article>
                        ))}
                    </div>
                    {n.options_section.note && (
                        <p className="np-note" dangerouslySetInnerHTML={{ __html: n.options_section.note }} />
                    )}
                </Section>
            )}

            {/* Modules */}
            {!n.html && n.modules_section && Array.isArray(n.modules_section.modules) && n.modules_section.modules.length > 0 && (
                <Section title={n.modules_section.title || 'Custom modules'} lead={n.modules_section.lead}>
                    <div className="np-modules">
                        {n.modules_section.modules.map((m, i) => (
                            <article key={i} className="np-module">
                                <div className="np-module-head">
                                    <span className="np-module-num">{m.num}</span>
                                    {m.tier_label && <span className="np-module-tier">{m.tier_label}</span>}
                                </div>
                                <h3 className="np-module-title">{m.title}</h3>
                                <p className="np-module-desc">{m.description}</p>
                                <div className="np-module-foot">
                                    {m.timeline && <span><strong>Timeline:</strong> {m.timeline}</span>}
                                    {m.mechanic && <span><strong>Build:</strong> {m.mechanic}</span>}
                                </div>
                            </article>
                        ))}
                    </div>
                </Section>
            )}

            {/* Timelines */}
            {!n.html && n.timelines_section && (
                <Section title={n.timelines_section.title || 'Delivery timelines'} lead={n.timelines_section.lead}>
                    {(n.timelines_section.timelines || []).map((t, i) => (
                        <div key={i} className="np-timeline">
                            <header className="np-timeline-head">
                                <h3 className="np-timeline-name">{t.name}</h3>
                                {t.sub && <span className="np-timeline-sub">{t.sub}</span>}
                            </header>
                            <ol className="np-timeline-steps">
                                {(t.steps || []).map((s, j) => (
                                    <li key={j} className="np-timeline-step">
                                        <span className="np-step-dot">{s.dot || (j + 1)}</span>
                                        <div className="np-step-body">
                                            <div className="np-step-head">
                                                <strong>{s.title}</strong>
                                                {s.when && <span className="np-step-when">{s.when}</span>}
                                            </div>
                                            <p>{s.body}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </Section>
            )}

            {/* Commercial — interactive on web, static summary in print */}
            <CommercialOptionsInteractive proposal={proposal} client={client} narrative={n} isPrint={isPrint} />

            {/* Why us */}
            {!n.html && n.why_section && (
                <Section title={n.why_section.title || 'Why us'}>
                    <div className="np-grid-3">
                        {(n.why_section.items || []).map((it, i) => (
                            <div key={i} className="np-card">
                                {it.icon && <span className="np-card-mark">{it.icon}</span>}
                                <h3 className="np-card-title">{it.title}</h3>
                                <p className="np-card-body">{it.body}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Next steps */}
            {!n.html && n.next_section && (
                <Section title={n.next_section.title || 'Next steps'} lead={n.next_section.lead}>
                    <div className="np-grid-3">
                        {(n.next_section.items || []).map((it, i) => (
                            <div key={i} className="np-card">
                                {it.mark && <span className="np-card-mark">{it.mark}</span>}
                                <h3 className="np-card-title">{it.title}</h3>
                                <p className="np-card-body">{it.body}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Footer */}
            <footer className="np-footer">
                <div className="np-container np-footer-inner">
                    <div>
                        <div className="np-footer-brand">{companyName}</div>
                        {companyAddress && <div className="np-footer-addr">{companyAddress}</div>}
                    </div>
                </div>
            </footer>
        </div>
    );
}

function Section({ title, lead, children }) {
    return (
        <section className="np-section">
            <div className="np-container">
                {title && <h2 className="np-section-title">{title}</h2>}
                {lead && <p className="np-section-lead">{lead}</p>}
                {children}
            </div>
        </section>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */

/**
 * Interactive commercial options block.
 * Always renders from `proposal.options` (the real pricing data). Allows the
 * viewer to pick Option A or B, then pick a payment plan (Monthly or Upfront
 * annual). If `discount_pct > 0` on the option the upfront plan shows the
 * saving copy. Options flagged `recommended` get a highlighted pill.
 */
function CommercialOptionsInteractive({ proposal, client, narrative, isPrint }) {
    const { token } = useParams();
    const options = useMemo(
        () => (proposal?.options || []).filter(Boolean),
        [proposal]
    );

    // Default the selection to the option already accepted, otherwise the
    // recommended one, otherwise the first.
    const initialId = useMemo(() => {
        if (proposal?.accepted_option_id) return proposal.accepted_option_id;
        const rec = options.find(o => o.recommended);
        if (rec?.id) return rec.id;
        return options[0]?.id || null;
    }, [options, proposal?.accepted_option_id]);

    const [selectedId, setSelectedId] = useState(initialId);
    const [plan, setPlan] = useState('monthly'); // 'monthly' | 'annual_upfront'
    // 'pick' = choose option + plan; 'signing' = signature form; 'done' = accepted + downloads
    const [step, setStep] = useState(proposal?.status === 'accepted' ? 'done' : 'pick');
    const [acceptedResult, setAcceptedResult] = useState(null);
    const sectionRef = useRef(null);

    useEffect(() => { setSelectedId(initialId); }, [initialId]);

    if (!options.length) return null;

    const selected = options.find(o => o.id === selectedId) || options[0];
    const cfg = narrative?.commercial_section || {};
    const title = cfg.title || 'Choose your investment';
    const lead = cfg.lead || 'Pick the option that fits — then choose how you’d like to pay.';

    // Print mode: render a static, complete summary of every option.
    if (isPrint) {
        return (
            <section className="np-section np-commercial-v2 np-print-summary" data-testid="np-commercial-options-print" data-pv-section="pricing">
                <div className="np-container">
                    <h2 className="np-section-title">{cfg.title || 'Commercial summary'}</h2>
                    {cfg.lead && <p className="np-section-lead">{cfg.lead}</p>}
                    <div className="np-co-grid">
                        {options.map((o) => (
                            <PrintOptionCard key={o.id} option={o} />
                        ))}
                    </div>
                    {cfg.note && (
                        <p className="np-note" dangerouslySetInnerHTML={{ __html: cfg.note }} />
                    )}
                </div>
            </section>
        );
    }

    const scrollHere = () => {
        if (sectionRef.current) sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const startSigning = () => {
        setStep('signing');
        setTimeout(scrollHere, 80);
    };
    const backToPick = () => {
        setStep('pick');
        setTimeout(scrollHere, 80);
    };

    return (
        <section
            ref={sectionRef}
            className="np-section np-commercial-v2"
            data-testid="np-commercial-options"
            data-pv-section="pricing"
            data-step={step}
        >
            <div className="np-container">
                <h2 className="np-section-title">{title}</h2>
                {lead && step === 'pick' && <p className="np-section-lead">{lead}</p>}

                {step === 'pick' && (
                    <>
                        <div className="np-co-grid">
                            {options.map((o) => (
                                <CommercialOptionCard
                                    key={o.id}
                                    option={o}
                                    selected={o.id === selected?.id}
                                    onSelect={() => setSelectedId(o.id)}
                                />
                            ))}
                        </div>

                        {selected && (
                            <CommercialPaymentPanel
                                option={selected}
                                plan={plan}
                                onPlanChange={setPlan}
                                onAccept={startSigning}
                            />
                        )}

                        {cfg.note && (
                            <p className="np-note" dangerouslySetInnerHTML={{ __html: cfg.note }} />
                        )}
                    </>
                )}

                {step === 'signing' && (
                    <NarrativeSignForm
                        token={token}
                        client={client}
                        option={selected}
                        plan={plan}
                        proposal={proposal}
                        onBack={backToPick}
                        onAccepted={(res) => {
                            setAcceptedResult(res);
                            setStep('done');
                            setTimeout(scrollHere, 80);
                        }}
                    />
                )}

                {step === 'done' && (
                    <NarrativeDoneCard
                        token={token}
                        option={selected}
                        plan={plan}
                        acceptedResult={acceptedResult}
                        proposal={proposal}
                    />
                )}
            </div>
        </section>
    );
}

function CommercialOptionCard({ option, selected, onSelect }) {
    const c = option.computed || {};
    const monthly = Number(c.monthly_total ?? option.monthly_saas_fee ?? 0);
    const annualGross = Number(c.annual_total_gross ?? (monthly * 12));
    const setup = Number(c.setup_total ?? option.setup_fee ?? 0);

    return (
        <button
            type="button"
            onClick={onSelect}
            className={`np-co-card ${selected ? 'is-selected' : ''} ${option.recommended ? 'is-recommended' : ''}`}
            data-testid={`np-co-option-${option.id}`}
            data-selected={selected ? 'true' : 'false'}
            aria-pressed={selected}
        >
            {option.recommended && (
                <span className="np-co-pill" data-testid={`np-co-recommended-${option.id}`}>Recommended</span>
            )}
            <span className="np-co-radio" aria-hidden="true">
                <span className="np-co-radio-dot" />
            </span>
            <h3 className="np-co-name">{option.name}</h3>
            {option.description && <p className="np-co-desc">{option.description}</p>}

            <div className="np-co-figures">
                <div className="np-co-fig">
                    <span className="np-co-fig-label">Monthly support fee</span>
                    <span className="np-co-fig-value">{GBP(monthly)}<span className="np-co-fig-per">/mo</span></span>
                </div>
                <div className="np-co-fig">
                    <span className="np-co-fig-label">Setup</span>
                    <span className="np-co-fig-value">{GBP(setup)}</span>
                </div>
                <div className="np-co-fig">
                    <span className="np-co-fig-label">Year-one recurring</span>
                    <span className="np-co-fig-value">{GBP(annualGross)}</span>
                </div>
            </div>
        </button>
    );
}

function PrintOptionCard({ option }) {
    const c = option.computed || {};
    const monthly = Number(c.monthly_total ?? option.monthly_saas_fee ?? 0);
    const annualGross = Number(c.annual_total_gross ?? (monthly * 12));
    const discountPct = Number(option.discount_pct ?? 0);
    const discountAmount = Number(c.discount_amount ?? (annualGross * discountPct / 100));
    const annualNet = Number(c.annual_total_net ?? annualGross);
    const setup = Number(c.setup_total ?? option.setup_fee ?? 0);
    const setupGross = Number(c.setup_total_gross ?? setup);
    const setupDiscPct = Number(c.setup_discount_pct ?? option.setup_discount_pct ?? 0);
    const setupDiscAmount = Number(c.setup_discount_amount ?? 0);
    const includeY2 = !!c.include_year_2_support;
    const y2Monthly = Number(c.year_2_monthly ?? 0);
    const y2Annual  = Number(c.year_2_annual ?? 0);
    const deposit = Number(c.deposit_due ?? 0);
    const vatApplies = option.vat_applies !== false;
    const term = Number(option.contract_length_months ?? 12);

    return (
        <div className={`np-co-card np-print-card ${option.recommended ? 'is-recommended' : ''}`}>
            {option.recommended && <span className="np-co-pill">Recommended</span>}
            <h3 className="np-co-name">{option.name}</h3>
            {option.description && <p className="np-co-desc">{option.description}</p>}

            <dl className="np-print-lines">
                {setupDiscAmount > 0 && (
                    <>
                        <div className="np-print-line"><dt>Setup before discount</dt><dd>{GBP(setupGross)}</dd></div>
                        <div className="np-print-line np-print-line-accent">
                            <dt>Setup fee discount</dt>
                            <dd>−{GBP(setupDiscAmount)} ({setupDiscPct}%)</dd>
                        </div>
                    </>
                )}
                <div className="np-print-line"><dt>Setup fee</dt><dd>{GBP(setup)}</dd></div>
                {deposit > 0 && <div className="np-print-line"><dt>Deposit on setup</dt><dd>{GBP(deposit)}</dd></div>}
                <div className="np-print-line"><dt>Monthly support fee (year 1)</dt><dd>{GBP(monthly)} / month</dd></div>
                <div className="np-print-line"><dt>Annual support fee (year 1)</dt><dd>{GBP(annualGross)}</dd></div>
                {discountPct > 0 && (
                    <>
                        <div className="np-print-line np-print-line-accent">
                            <dt>Discount recurring support</dt>
                            <dd>−{GBP(discountAmount)} ({discountPct}%)</dd>
                        </div>
                        <div className="np-print-line">
                            <dt>If paid upfront annually</dt>
                            <dd><strong>{GBP(annualNet)}</strong></dd>
                        </div>
                    </>
                )}
                {includeY2 && y2Annual > 0 && (
                    <>
                        <div className="np-print-line"><dt>Year 2 support (monthly)</dt><dd>{GBP(y2Monthly)} / month</dd></div>
                        <div className="np-print-line"><dt>Year 2 support (annual)</dt><dd>{GBP(y2Annual)}</dd></div>
                    </>
                )}
                <div className="np-print-line"><dt>Contract length</dt><dd>{term} months</dd></div>
                <div className="np-print-line"><dt>VAT</dt><dd>{vatApplies ? 'Applies (20%)' : 'Not applicable'}</dd></div>
            </dl>
        </div>
    );
}


function CommercialPaymentPanel({ option, plan, onPlanChange, onAccept }) {
    const c = option.computed || {};
    const monthly = Number(c.monthly_total ?? option.monthly_saas_fee ?? 0);
    const annualGross = Number(c.annual_total_gross ?? (monthly * 12));
    const discountPct = Number(option.discount_pct ?? 0);
    const discountAmount = Number(c.discount_amount ?? (annualGross * discountPct / 100));
    const annualNet = Number(c.annual_total_net ?? (annualGross - discountAmount));
    const setup = Number(c.setup_total ?? option.setup_fee ?? 0);
    const setupGross = Number(c.setup_total_gross ?? setup);
    const setupDiscAmount = Number(c.setup_discount_amount ?? 0);
    const setupDiscPct = Number(c.setup_discount_pct ?? option.setup_discount_pct ?? 0);
    const includeY2 = !!c.include_year_2_support;
    const y2Monthly = Number(c.year_2_monthly ?? 0);
    const y2Annual = Number(c.year_2_annual ?? 0);
    const deposit = Number(c.deposit_due ?? 0);
    const vatApplies = option.vat_applies !== false;
    const vatAmt = Number(c.vat_amount ?? 0);
    const hasDiscount = discountPct > 0 && discountAmount > 0;
    const isUpfront = plan === 'annual_upfront';

    // Recurring shown in chosen plan
    const recurringHeadline = isUpfront ? annualNet : monthly;
    const recurringCaption = isUpfront
        ? 'paid upfront, then renews annually'
        : 'per month, billed monthly';

    return (
        <div className="np-pay-panel" data-testid="np-pay-panel" data-plan={plan}>
            <div className="np-pay-head">
                <div>
                    <span className="np-pay-eyebrow">Selected · {option.name}</span>
                    <h3 className="np-pay-title">How would you like to pay the monthly support fee?</h3>
                </div>
            </div>

            <div className="np-pay-tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={!isUpfront}
                    className={`np-pay-tab ${!isUpfront ? 'is-active' : ''}`}
                    onClick={() => onPlanChange('monthly')}
                    data-testid="np-pay-tab-monthly"
                >
                    <span className="np-pay-tab-label">Pay monthly</span>
                    <span className="np-pay-tab-sub">{GBP(monthly)} / month</span>
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={isUpfront}
                    className={`np-pay-tab ${isUpfront ? 'is-active' : ''}`}
                    onClick={() => onPlanChange('annual_upfront')}
                    data-testid="np-pay-tab-upfront"
                >
                    <span className="np-pay-tab-label">
                        Pay upfront (annual)
                        {hasDiscount && <span className="np-pay-save-flag">Save {discountPct}%</span>}
                    </span>
                    <span className="np-pay-tab-sub">{GBP(annualNet)} / year</span>
                </button>
            </div>

            <div className="np-pay-summary">
                {setupDiscAmount > 0 && (
                    <>
                        <div className="np-pay-row np-pay-row-muted">
                            <span>Setup before discount</span>
                            <strong>{GBP(setupGross)}</strong>
                        </div>
                        <div className="np-pay-row np-pay-row-muted" data-testid="np-pay-setup-discount">
                            <span>Setup fee discount ({setupDiscPct}%)</span>
                            <strong>− {GBP(setupDiscAmount)}</strong>
                        </div>
                    </>
                )}
                <div className="np-pay-row">
                    <span>Setup</span>
                    <strong>{GBP(setup)}</strong>
                </div>
                {deposit > 0 && (
                    <div className="np-pay-row np-pay-row-muted">
                        <span>Deposit due on setup</span>
                        <strong>{GBP(deposit)}</strong>
                    </div>
                )}
                <div className="np-pay-row">
                    <span>{isUpfront ? 'Annual support fee' : 'Monthly support fee'}</span>
                    <strong>
                        {GBP(recurringHeadline)}
                        <span className="np-pay-row-sub"> {recurringCaption}</span>
                    </strong>
                </div>

                {isUpfront && hasDiscount && (
                    <div className="np-pay-discount" data-testid="np-pay-discount">
                        <span className="np-pay-discount-pill">{discountPct}% discount recurring support</span>
                        <span className="np-pay-discount-text">
                            You save <strong>{GBP(discountAmount)}</strong> when paid upfront.
                            <span className="np-pay-discount-strike"> Was {GBP(annualGross)}/yr.</span>
                        </span>
                    </div>
                )}

                {includeY2 && y2Annual > 0 && (
                    <div className="np-pay-row np-pay-row-muted" data-testid="np-pay-y2">
                        <span>Year 2 support onwards</span>
                        <strong>
                            {GBP(y2Monthly)} / mo
                            <span className="np-pay-row-sub"> ({GBP(y2Annual)} / yr · support only — no implementation)</span>
                        </strong>
                    </div>
                )}

                {vatApplies && vatAmt > 0 && (
                    <div className="np-pay-row np-pay-row-muted">
                        <span>VAT (year one)</span>
                        <strong>{GBP(vatAmt)}</strong>
                    </div>
                )}

                <div className="np-pay-row np-pay-row-total">
                    <span>Year-one total{vatApplies ? ' (incl. VAT)' : ''}</span>
                    <strong>{GBP(setup + (isUpfront ? annualNet : annualGross) + (vatApplies ? vatAmt : 0))}</strong>
                </div>
            </div>

            <div className="np-pay-cta-row">
                <button
                    type="button"
                    onClick={onAccept}
                    className="np-pay-cta"
                    data-testid="np-pay-accept-cta"
                >
                    Accept this option &amp; sign →
                </button>
                <span className="np-pay-cta-sub">You'll sign once and instantly download the signed pack.</span>
            </div>
        </div>
    );
}

/* ──────────────────────────────────────────────────────────────────────── */

/**
 * Inline signature form for the narrative flow. Captures signer details +
 * signature and posts to /api/public/proposals/:token/accept which atomically
 * flips the proposal to accepted, creates the contract and returns it.
 */
function NarrativeSignForm({ token, client, option, plan, proposal, onBack, onAccepted }) {
    const [acceptance, setAcceptance] = useState({
        signer_name: '',
        signer_title: '',
        signer_email: '',
        signer_company: client?.company_name || '',
        typed_signature: '',
        signature_image_base64: null,
        accept_terms: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Pick the default hosting option (first visible) — the existing API uses
    // it if hosting_option_id is null too, but we send it explicitly for clarity.
    const defaultHostingId = useMemo(() => {
        const visible = (proposal?.hosting_options || []).filter(h => h.visible_on_proposal !== false);
        return visible[0]?.id || null;
    }, [proposal]);

    const c = option.computed || {};
    const isUpfront = plan === 'annual_upfront';
    const monthly = Number(c.monthly_total ?? option.monthly_saas_fee ?? 0);
    const annualGross = Number(c.annual_total_gross ?? (monthly * 12));
    const annualNet = Number(c.annual_total_net ?? annualGross);
    const setup = Number(c.setup_total ?? option.setup_fee ?? 0);
    const planLabel = isUpfront ? 'Pay upfront (annual)' : 'Pay monthly';
    const planValue = isUpfront ? `${GBP(annualNet)} / year` : `${GBP(monthly)} / month`;

    const submit = async (e) => {
        e.preventDefault();
        setFormError('');
        const fail = (msg) => { setFormError(msg); toast.error(msg); };
        if (!acceptance.signer_name.trim()) return fail('Please enter your full name.');
        if (!acceptance.signer_email.trim()) return fail('Please enter your email.');
        if (!acceptance.typed_signature.trim()) return fail('Please type your signature for the record.');
        if (!acceptance.accept_terms) return fail('Please tick the box to confirm you accept the terms.');

        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/public/proposals/${token}/accept`, {
                option_id: option.id,
                hosting_option_id: defaultHostingId,
                signer_name: acceptance.signer_name.trim(),
                signer_title: acceptance.signer_title.trim() || null,
                signer_email: acceptance.signer_email.trim(),
                signer_company: acceptance.signer_company.trim() || null,
                typed_signature: acceptance.typed_signature.trim(),
                signature_image_base64: acceptance.signature_image_base64,
                accept_terms: true,
                payment_plan: plan,
            });
            toast.success('Proposal accepted — welcome aboard.');
            onAccepted(data);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Unable to submit acceptance.';
            setFormError(typeof msg === 'string' ? msg : 'Unable to submit acceptance.');
            toast.error(typeof msg === 'string' ? msg : 'Unable to submit acceptance.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="np-sign-form" onSubmit={submit} data-testid="np-sign-form" data-pv-section="acceptance">
            <button
                type="button"
                onClick={onBack}
                className="np-sign-back"
                data-testid="np-sign-back-btn"
            >
                <ArrowLeft size={14} /> Change option
            </button>

            <div className="np-sign-summary">
                <div className="np-sign-summary-row">
                    <span className="np-sign-summary-label">You're accepting</span>
                    <strong className="np-sign-summary-value">{option.name}</strong>
                </div>
                <div className="np-sign-summary-row">
                    <span className="np-sign-summary-label">Payment plan</span>
                    <strong className="np-sign-summary-value">{planLabel} · {planValue}</strong>
                </div>
                <div className="np-sign-summary-row">
                    <span className="np-sign-summary-label">Setup</span>
                    <strong className="np-sign-summary-value">{GBP(setup)}</strong>
                </div>
            </div>

            <a
                href={`/p/${token}/terms`}
                target="_blank"
                rel="noopener noreferrer"
                className="np-terms-cta"
                data-testid="np-sign-terms-cta"
            >
                <FileText size={15} />
                <span className="np-terms-cta-body">
                    <strong>Read &amp; download the full Terms &amp; Conditions</strong>
                    <span>Opens in a new tab — download as PDF before you sign.</span>
                </span>
                <span className="np-terms-cta-arrow" aria-hidden="true">→</span>
            </a>

            <h3 className="np-sign-section-title">Your details</h3>
            <div className="np-sign-grid">
                <NPField label="Full name *" value={acceptance.signer_name}
                    onChange={v => setAcceptance(a => ({ ...a, signer_name: v }))}
                    testId="np-sign-name" />
                <NPField label="Job title" value={acceptance.signer_title}
                    onChange={v => setAcceptance(a => ({ ...a, signer_title: v }))}
                    testId="np-sign-title" />
                <NPField label="Email *" value={acceptance.signer_email} type="email"
                    onChange={v => setAcceptance(a => ({ ...a, signer_email: v }))}
                    testId="np-sign-email" />
                <NPField label="Company" value={acceptance.signer_company}
                    onChange={v => setAcceptance(a => ({ ...a, signer_company: v }))}
                    testId="np-sign-company" />
            </div>

            <h3 className="np-sign-section-title">Signature</h3>
            <div className="np-sign-pad">
                <SignaturePad
                    onDrawnChange={(dataUrl) => setAcceptance(a => ({ ...a, signature_image_base64: dataUrl }))}
                    onTypedChange={(v) => setAcceptance(a => ({ ...a, typed_signature: v }))}
                    typedValue={acceptance.typed_signature}
                />
            </div>
            <NPField
                label="Typed signature for the record *"
                value={acceptance.typed_signature}
                onChange={v => setAcceptance(a => ({ ...a, typed_signature: v }))}
                placeholder="Type your full name"
                testId="np-sign-typed"
            />

            <label className="np-sign-terms" data-testid="np-sign-terms-label">
                <input
                    type="checkbox"
                    checked={acceptance.accept_terms}
                    onChange={(e) => setAcceptance(a => ({ ...a, accept_terms: e.target.checked }))}
                    data-testid="np-sign-terms-checkbox"
                />
                <span>
                    I confirm I have authority to bind <strong>{acceptance.signer_company || client?.company_name || 'the client'}</strong> to this agreement
                    and I accept the commercial option, payment plan and the
                    {' '}
                    <a
                        href={`/p/${token}/terms`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="np-terms-link"
                        data-testid="np-sign-view-terms-link"
                    >
                        Terms &amp; Conditions
                    </a>
                    {' '}
                    set out in this proposal.
                </span>
            </label>

            <div className="np-sign-footer">
                <span className="np-sign-audit"><Lock size={12} /> Signed timestamp, IP and device are recorded for audit.</span>
                <button
                    type="submit"
                    disabled={submitting}
                    className="np-pay-cta"
                    data-testid="np-sign-submit-btn"
                >
                    <ShieldCheck size={15} style={{ marginRight: 6 }} />
                    {submitting ? 'Submitting…' : 'Accept & Sign'}
                </button>
            </div>
            {formError && (
                <div className="np-sign-error" data-testid="np-sign-error" role="alert">
                    {formError}
                </div>
            )}
        </form>
    );
}

function NPField({ label, value, onChange, type = 'text', placeholder, testId }) {
    return (
        <label className="np-field">
            <span className="np-field-label">{label}</span>
            <input
                type={type}
                value={value || ''}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="np-field-input"
                data-testid={testId}
            />
        </label>
    );
}

/**
 * After acceptance — show confirmation + download links for the signed pack
 * (proposal PDF + signed agreement PDF).
 */
function NarrativeDoneCard({ token, option, plan, acceptedResult, proposal }) {
    const proposalPdf = `${API}/public/proposals/${token}/narrative-pdf`;
    const contractPdf = `${API}/public/proposals/${token}/contract-pdf`;
    const contractNumber =
        acceptedResult?.contract_number ||
        proposal?.contract_number ||
        null;
    const acceptedAt = proposal?.accepted_at ? new Date(proposal.accepted_at) : new Date();
    const isUpfront = plan === 'annual_upfront';

    return (
        <div className="np-done" data-testid="np-done-card">
            <div className="np-done-mark"><CheckCircle2 size={28} /></div>
            <h3 className="np-done-title">Thank you — your proposal has been accepted.</h3>
            <p className="np-done-sub">
                You chose <strong>{option.name}</strong> · {isUpfront ? 'paying annually upfront' : 'paying monthly'}.
                {contractNumber && <> Your signed agreement <strong>{contractNumber}</strong> is ready.</>}
                <br />
                <span className="np-done-when">Signed {acceptedAt.toLocaleString('en-GB')}.</span>
            </p>

            <div className="np-done-actions">
                <a
                    href={contractPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="np-pay-cta"
                    data-testid="np-done-contract-pdf"
                >
                    <FileDown size={15} style={{ marginRight: 8 }} />
                    Download signed agreement
                </a>
                <a
                    href={proposalPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="np-done-secondary"
                    data-testid="np-done-proposal-pdf"
                >
                    <FileDown size={14} style={{ marginRight: 6 }} />
                    Download proposal pack
                </a>
            </div>

            <p className="np-done-foot">
                We've emailed a copy to <strong>{proposal?.acceptance?.signer_email || 'you'}</strong>.
                Our team will be in touch shortly to kick things off.
            </p>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */

const NARRATIVE_CSS = `
.np-root {
    --np-bg: #fafaf7;
    --np-fg: #1a1a1a;
    --np-muted: #6b6b6b;
    --np-border: #e7e5dc;
    --np-card: #ffffff;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background: var(--np-bg);
    color: var(--np-fg);
    line-height: 1.55;
}
.np-container { max-width: 1100px; margin: 0 auto; padding: 0 32px; }

/* Header */
.np-header { background: #fff; border-bottom: 1px solid var(--np-border); padding: 18px 0; position: sticky; top: 0; z-index: 5; }
.np-header-inner { max-width: 1100px; margin: 0 auto; padding: 0 32px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
.np-logo { height: 32px; width: auto; }
.np-brand-name { font-weight: 700; letter-spacing: -0.01em; color: var(--np-brand); font-size: 18px; }
.np-header-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.np-meta-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--np-muted); }
.np-meta-value { font-size: 14px; font-weight: 600; color: var(--np-brand); }

/* Sections */
.np-section { padding: 88px 0; border-bottom: 1px solid var(--np-border); }
.np-section:last-of-type { border-bottom: none; }
.np-section-title { font-size: 30px; font-weight: 700; letter-spacing: -0.02em; color: var(--np-fg); margin: 0 0 16px; }
.np-section-lead { font-size: 17px; color: var(--np-muted); max-width: 720px; margin: 0 0 48px; }

/* Hero */
.np-hero { padding: 120px 0 88px; background: linear-gradient(180deg, #fff 0%, var(--np-bg) 100%); }
.np-eyebrow { font-size: 12px; text-transform: uppercase; letter-spacing: 0.18em; color: var(--np-brand); font-weight: 700; }
.np-headline { font-size: clamp(36px, 5vw, 58px); line-height: 1.05; letter-spacing: -0.025em; font-weight: 700; margin: 16px 0 24px; max-width: 900px; }
.np-headline em { font-style: italic; color: var(--np-brand); font-weight: 700; }
.np-sub { font-size: 19px; color: var(--np-muted); max-width: 680px; margin: 0 0 56px; }
.np-meta-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0; border-top: 1px solid var(--np-border); border-left: 1px solid var(--np-border); }
.np-meta-card { padding: 20px 24px; border-right: 1px solid var(--np-border); border-bottom: 1px solid var(--np-border); background: #fff; display: flex; flex-direction: column; gap: 4px; }
.np-meta-card-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--np-muted); }
.np-meta-card-value { font-size: 14px; font-weight: 600; color: var(--np-fg); }

/* Cards */
.np-grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; }
.np-card { background: var(--np-card); border: 1px solid var(--np-border); padding: 28px; }
.np-card-mark { display: inline-block; width: 30px; height: 30px; line-height: 30px; text-align: center; background: var(--np-brand); color: #fff; font-weight: 700; font-size: 13px; margin-bottom: 16px; }
.np-card-title { font-size: 17px; font-weight: 700; margin: 0 0 8px; letter-spacing: -0.01em; }
.np-card-body { font-size: 14.5px; color: var(--np-muted); margin: 0; line-height: 1.6; }

/* Pull quote */
.np-quote { border-left: 3px solid var(--np-brand); padding: 24px 28px; margin: 56px 0 0; font-size: 21px; font-style: italic; color: var(--np-fg); background: #fff; }

/* Options */
.np-grid-options { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 28px; }
.np-option { background: #fff; border: 1px solid var(--np-border); padding: 36px 32px; position: relative; display: flex; flex-direction: column; gap: 16px; }
.np-option.is-featured { border-color: var(--np-brand); border-width: 2px; box-shadow: 0 12px 40px -8px rgba(0,0,0,0.08); }
.np-option-badge { position: absolute; top: -10px; left: 24px; background: var(--np-brand); color: #fff; padding: 4px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; }
.np-option-tag { font-size: 11px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--np-muted); font-weight: 600; }
.np-option-name { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin: 0; }
.np-option-name em { font-style: italic; color: var(--np-brand); }
.np-option-tagline { font-size: 14px; color: var(--np-muted); margin: -8px 0 0; }
.np-option-price-block { padding-top: 12px; border-top: 1px solid var(--np-border); display: flex; flex-direction: column; gap: 2px; }
.np-option-price { font-size: 38px; font-weight: 700; letter-spacing: -0.02em; color: var(--np-fg); line-height: 1; }
.np-option-price-per { font-size: 13px; color: var(--np-muted); }
.np-option-price-sub { font-size: 12px; color: var(--np-muted); font-style: italic; margin-top: 4px; }
.np-option-monthly { padding: 12px 16px; background: var(--np-bg); border-left: 3px solid var(--np-accent); display: flex; flex-direction: column; }
.np-option-monthly-amount { font-size: 16px; font-weight: 700; }
.np-option-monthly-sub { font-size: 12px; color: var(--np-muted); }
.np-option-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.np-option-features li { font-size: 14px; padding-left: 22px; position: relative; color: var(--np-fg); }
.np-option-features li::before { content: ""; position: absolute; left: 0; top: 8px; width: 12px; height: 2px; background: var(--np-brand); }
.np-option-timeline { font-size: 12px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--np-muted); font-weight: 600; padding-top: 12px; border-top: 1px solid var(--np-border); margin: 0; }
.np-option-footer { font-size: 13px; color: var(--np-muted); font-style: italic; margin: 0; }

/* Note */
.np-note { margin-top: 40px; padding: 20px 24px; background: #fff; border-left: 3px solid var(--np-accent); font-size: 14px; color: var(--np-fg); }
.np-note b { color: var(--np-brand); }

/* Modules */
.np-modules { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1px; background: var(--np-border); border: 1px solid var(--np-border); }
.np-module { background: #fff; padding: 28px; display: flex; flex-direction: column; gap: 10px; }
.np-module-head { display: flex; justify-content: space-between; align-items: center; }
.np-module-num { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--np-brand); font-weight: 700; }
.np-module-tier { font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--np-muted); padding: 2px 8px; background: var(--np-bg); }
.np-module-title { font-size: 16px; font-weight: 700; letter-spacing: -0.01em; margin: 0; }
.np-module-desc { font-size: 14px; color: var(--np-muted); margin: 0; line-height: 1.55; flex: 1; }
.np-module-foot { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--np-muted); padding-top: 12px; border-top: 1px dashed var(--np-border); }
.np-module-foot strong { color: var(--np-fg); font-weight: 600; }

/* Timelines */
.np-timeline + .np-timeline { margin-top: 48px; }
.np-timeline-head { display: flex; align-items: baseline; gap: 16px; padding-bottom: 16px; border-bottom: 1px solid var(--np-border); margin-bottom: 24px; }
.np-timeline-name { font-size: 19px; font-weight: 700; margin: 0; }
.np-timeline-sub { font-size: 12px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--np-muted); }
.np-timeline-steps { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 20px; }
.np-timeline-step { display: flex; gap: 20px; align-items: flex-start; }
.np-step-dot { flex-shrink: 0; width: 36px; height: 36px; line-height: 36px; text-align: center; background: var(--np-brand); color: #fff; font-weight: 700; font-size: 12px; font-family: 'JetBrains Mono', monospace; }
.np-step-body { flex: 1; background: #fff; padding: 16px 20px; border: 1px solid var(--np-border); }
.np-step-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 4px; }
.np-step-head strong { font-size: 15px; }
.np-step-when { font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--np-muted); }
.np-step-body p { font-size: 13.5px; color: var(--np-muted); margin: 0; line-height: 1.55; }

/* Commercial */
.np-commercial-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 28px; }
.np-commercial-card { background: #fff; border: 1px solid var(--np-border); padding: 28px; }
.np-commercial-title { font-size: 18px; font-weight: 700; letter-spacing: -0.01em; margin: 0; }
.np-commercial-sub { font-size: 12px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--np-muted); margin: 4px 0 24px; }
.np-commercial-lines { margin: 0; padding: 0; }
.np-commercial-line { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px dashed var(--np-border); font-size: 14px; }
.np-commercial-line dt { color: var(--np-muted); margin: 0; }
.np-commercial-line dd { font-weight: 600; color: var(--np-fg); margin: 0; }
.np-commercial-total { display: flex; justify-content: space-between; padding-top: 16px; margin-top: 12px; border-top: 2px solid var(--np-brand); font-size: 14px; }
.np-commercial-total span { text-transform: uppercase; letter-spacing: 0.12em; font-size: 12px; color: var(--np-muted); }
.np-commercial-total strong { font-size: 22px; color: var(--np-brand); }

/* Interactive commercial v2 */
.np-co-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; margin-bottom: 32px; }
.np-co-card {
    position: relative; text-align: left; cursor: pointer;
    background: #fff; border: 1.5px solid var(--np-border); padding: 28px 26px 24px;
    display: flex; flex-direction: column; gap: 14px;
    transition: border-color .2s, box-shadow .2s, transform .15s;
    font: inherit; color: inherit; appearance: none; outline: none; width: 100%;
}
.np-co-card:hover { border-color: var(--np-brand); transform: translateY(-1px); }
.np-co-card.is-selected { border-color: var(--np-brand); border-width: 2px; box-shadow: 0 10px 30px -10px rgba(0,47,167,0.25); }
.np-co-card.is-recommended { padding-top: 36px; }
.np-co-pill {
    position: absolute; top: -12px; left: 24px;
    background: var(--np-accent); color: #fff;
    padding: 5px 12px; font-size: 10.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.14em; border-radius: 999px;
    box-shadow: 0 4px 12px -2px rgba(0,0,0,0.15);
}
.np-co-radio {
    position: absolute; top: 20px; right: 20px;
    width: 22px; height: 22px; border-radius: 50%;
    border: 2px solid var(--np-border); background: #fff;
    display: flex; align-items: center; justify-content: center;
    transition: border-color .2s;
}
.np-co-card.is-selected .np-co-radio { border-color: var(--np-brand); }
.np-co-radio-dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: transparent; transition: background .15s;
}
.np-co-card.is-selected .np-co-radio-dot { background: var(--np-brand); }
.np-co-name { font-size: 19px; font-weight: 700; letter-spacing: -0.015em; margin: 0; padding-right: 38px; }
.np-co-desc { font-size: 13.5px; color: var(--np-muted); margin: 0; line-height: 1.55; }
.np-co-figures { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 18px; margin-top: 4px; padding-top: 16px; border-top: 1px dashed var(--np-border); }
.np-co-fig { display: flex; flex-direction: column; gap: 2px; }
.np-co-fig:first-child { grid-column: 1 / -1; }
.np-co-fig-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--np-muted); font-weight: 600; }
.np-co-fig-value { font-size: 22px; font-weight: 700; color: var(--np-fg); letter-spacing: -0.015em; line-height: 1.1; }
.np-co-fig:not(:first-child) .np-co-fig-value { font-size: 15px; }
.np-co-fig-per { font-size: 12px; color: var(--np-muted); font-weight: 500; margin-left: 3px; }

/* Payment panel */
.np-pay-panel { background: #fff; border: 1px solid var(--np-border); padding: 28px 28px 24px; }
.np-pay-head { margin-bottom: 18px; }
.np-pay-eyebrow { font-size: 11px; text-transform: uppercase; letter-spacing: 0.16em; color: var(--np-brand); font-weight: 700; }
.np-pay-title { font-size: 18px; font-weight: 700; letter-spacing: -0.015em; margin: 6px 0 0; }
.np-pay-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
.np-pay-tab {
    background: var(--np-bg); border: 1.5px solid var(--np-border);
    padding: 16px 18px; cursor: pointer; text-align: left;
    display: flex; flex-direction: column; gap: 4px;
    font: inherit; color: inherit; appearance: none; outline: none;
    transition: border-color .2s, background .2s;
}
.np-pay-tab:hover { border-color: var(--np-brand); }
.np-pay-tab.is-active { border-color: var(--np-brand); background: #fff; box-shadow: inset 0 0 0 1px var(--np-brand); }
.np-pay-tab-label { font-size: 14px; font-weight: 700; color: var(--np-fg); display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.np-pay-tab-sub { font-size: 13px; color: var(--np-muted); }
.np-pay-save-flag {
    display: inline-block; background: var(--np-accent); color: #fff;
    font-size: 10px; padding: 2px 8px; border-radius: 999px;
    text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700;
}

/* Payment summary rows */
.np-pay-summary { display: flex; flex-direction: column; gap: 10px; }
.np-pay-row { display: flex; justify-content: space-between; align-items: baseline; padding: 10px 0; border-bottom: 1px dashed var(--np-border); font-size: 14px; }
.np-pay-row strong { font-weight: 700; color: var(--np-fg); font-size: 15px; }
.np-pay-row span { color: var(--np-fg); }
.np-pay-row-muted span, .np-pay-row-muted strong { color: var(--np-muted); font-weight: 500; }
.np-pay-row-sub { font-size: 12px; color: var(--np-muted); font-weight: 500; margin-left: 4px; }
.np-pay-row-total { border-bottom: none; border-top: 2px solid var(--np-brand); padding-top: 16px; margin-top: 6px; }
.np-pay-row-total span { text-transform: uppercase; font-size: 12px; letter-spacing: 0.12em; color: var(--np-muted); font-weight: 600; }
.np-pay-row-total strong { font-size: 24px; color: var(--np-brand); letter-spacing: -0.02em; }

/* Discount banner */
.np-pay-discount {
    display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
    background: linear-gradient(90deg, color-mix(in srgb, var(--np-accent) 14%, #fff) 0%, #fff 90%);
    border-left: 3px solid var(--np-accent);
    padding: 14px 18px; margin: 4px 0;
}
.np-pay-discount-pill {
    background: var(--np-accent); color: #fff;
    padding: 4px 10px; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.12em; border-radius: 999px;
}
.np-pay-discount-text { font-size: 13.5px; color: var(--np-fg); }
.np-pay-discount-text strong { color: var(--np-accent); font-weight: 700; }
.np-pay-discount-strike { color: var(--np-muted); text-decoration: line-through; font-size: 12.5px; margin-left: 4px; }

/* CTA row */
.np-pay-cta-row { margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--np-border); display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
.np-pay-cta {
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--np-brand); color: #fff !important;
    padding: 14px 26px; font-size: 15px; font-weight: 700;
    letter-spacing: -0.005em; text-decoration: none;
    border: 2px solid var(--np-brand);
    transition: background .2s, transform .15s, box-shadow .2s;
    cursor: pointer;
}
.np-pay-cta:hover {
    background: color-mix(in srgb, var(--np-brand) 85%, #000);
    transform: translateY(-1px);
    box-shadow: 0 8px 20px -6px rgba(0,47,167,0.4);
}
.np-pay-cta-sub { font-size: 12px; color: var(--np-muted); }
.np-pay-accepted {
    background: color-mix(in srgb, var(--np-accent) 14%, #fff);
    color: var(--np-brand); font-weight: 700; font-size: 14px;
    padding: 14px 18px; border-left: 3px solid var(--np-accent); width: 100%;
}

/* Inline sign form */
.np-sign-form { background: #fff; border: 1px solid var(--np-border); padding: 32px 32px 28px; display: flex; flex-direction: column; gap: 18px; }
.np-sign-back {
    align-self: flex-start;
    display: inline-flex; align-items: center; gap: 6px;
    background: transparent; border: 1px solid var(--np-border);
    color: var(--np-fg); font-size: 12px; font-weight: 600;
    padding: 7px 12px; cursor: pointer; appearance: none;
    text-transform: uppercase; letter-spacing: 0.08em;
    transition: border-color .15s, background .15s;
}
.np-sign-back:hover { border-color: var(--np-brand); background: var(--np-bg); }

.np-sign-summary { display: flex; flex-direction: column; gap: 6px; padding: 16px 18px; background: var(--np-bg); border-left: 3px solid var(--np-brand); }
.np-sign-summary-row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; font-size: 13px; }
.np-sign-summary-label { color: var(--np-muted); text-transform: uppercase; letter-spacing: 0.1em; font-size: 11px; font-weight: 600; }
.np-sign-summary-value { font-size: 14px; font-weight: 700; color: var(--np-fg); }

.np-sign-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: var(--np-brand); margin: 12px 0 0; }

.np-sign-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
.np-field { display: flex; flex-direction: column; gap: 6px; }
.np-field-label { font-size: 11.5px; color: var(--np-muted); font-weight: 600; }
.np-field-input {
    appearance: none; font: inherit; color: var(--np-fg);
    background: #fff; border: 1.5px solid var(--np-border);
    padding: 11px 13px; font-size: 14px; outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.np-field-input:focus { border-color: var(--np-brand); box-shadow: 0 0 0 3px color-mix(in srgb, var(--np-brand) 12%, transparent); }

.np-sign-pad :is(canvas, .signature-canvas) { background: #fff; }

.np-sign-terms { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; background: var(--np-bg); cursor: pointer; font-size: 13px; line-height: 1.55; color: var(--np-fg); }
.np-sign-terms input[type="checkbox"] { margin-top: 2px; width: 16px; height: 16px; accent-color: var(--np-brand); cursor: pointer; }
.np-terms-link { color: var(--np-brand); font-weight: 700; text-decoration: underline; text-underline-offset: 2px; }
.np-terms-link:hover { color: color-mix(in srgb, var(--np-brand) 80%, #000); }

/* Terms CTA banner — directs the client to read the full T&Cs before signing */
.np-terms-cta {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px; margin-top: 4px;
    background: linear-gradient(90deg, color-mix(in srgb, var(--np-brand) 6%, #fff) 0%, #fff 100%);
    border: 1.5px solid var(--np-brand);
    border-left-width: 4px;
    color: var(--np-fg); text-decoration: none;
    transition: background .15s, transform .12s, box-shadow .15s;
}
.np-terms-cta:hover {
    background: color-mix(in srgb, var(--np-brand) 10%, #fff);
    transform: translateY(-1px);
    box-shadow: 0 6px 18px -8px rgba(0,47,167,0.3);
}
.np-terms-cta svg { color: var(--np-brand); flex-shrink: 0; }
.np-terms-cta-body { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.np-terms-cta-body strong { font-size: 14px; font-weight: 700; color: var(--np-brand); letter-spacing: -0.005em; }
.np-terms-cta-body span { font-size: 12.5px; color: var(--np-muted); }
.np-terms-cta-arrow { color: var(--np-brand); font-size: 18px; font-weight: 700; flex-shrink: 0; }

.np-sign-footer { display: flex; justify-content: space-between; align-items: center; gap: 14px; flex-wrap: wrap; padding-top: 12px; border-top: 1px solid var(--np-border); }
.np-sign-audit { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--np-muted); }
.np-sign-error {
    background: #fff4f4; border-left: 3px solid #d4183d;
    color: #c01233; font-size: 13.5px; font-weight: 600;
    padding: 12px 16px; margin-top: 4px;
}

/* Done card */
.np-done { background: #fff; border: 1px solid var(--np-border); padding: 40px 36px; display: flex; flex-direction: column; align-items: flex-start; gap: 14px; }
.np-done-mark { width: 56px; height: 56px; border-radius: 50%; background: color-mix(in srgb, var(--np-accent) 18%, #fff); color: var(--np-accent); display: flex; align-items: center; justify-content: center; }
.np-done-title { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin: 0; color: var(--np-fg); }
.np-done-sub { font-size: 14.5px; color: var(--np-fg); margin: 0; line-height: 1.6; }
.np-done-when { color: var(--np-muted); font-size: 12.5px; }
.np-done-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 6px; }
.np-done-secondary {
    display: inline-flex; align-items: center;
    padding: 14px 22px; font-size: 14px; font-weight: 600;
    text-decoration: none; color: var(--np-fg);
    border: 2px solid var(--np-border); background: #fff;
    transition: border-color .15s, color .15s;
}
.np-done-secondary:hover { border-color: var(--np-brand); color: var(--np-brand); }
.np-done-foot { font-size: 12.5px; color: var(--np-muted); margin: 14px 0 0; }

/* Custom HTML mode — user's pasted HTML is rendered raw. We only neutralise
   our default body margin so the author's layout sits flush. */
.np-html-wrap { width: 100%; }
.np-html-wrap > * { max-width: 100%; }

/* Print mode */
.np-root.np-print .np-header { position: static; }
.np-root.np-print .np-section { padding: 48px 0; page-break-inside: avoid; }
.np-root.np-print .np-hero { padding: 64px 0 32px; }
.np-print-summary .np-co-grid { grid-template-columns: 1fr 1fr; gap: 18px; }
.np-print-card { cursor: default; padding: 28px 26px; display: flex; flex-direction: column; gap: 12px; }
.np-print-card.is-recommended { border: 2px solid var(--np-brand); padding-top: 36px; }
.np-print-lines { margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0; }
.np-print-line { display: flex; justify-content: space-between; padding: 9px 0; border-bottom: 1px dashed var(--np-border); font-size: 13.5px; }
.np-print-line dt { color: var(--np-muted); margin: 0; }
.np-print-line dd { color: var(--np-fg); font-weight: 600; margin: 0; }
.np-print-line-accent dt, .np-print-line-accent dd { color: var(--np-accent); }
.np-print-line strong { color: var(--np-brand); font-size: 15px; }

@media print {
    .np-header { position: static !important; }
    .np-section { page-break-inside: avoid; }
}

@media (max-width: 640px) {
    .np-co-figures { grid-template-columns: 1fr; }
    .np-pay-tabs { grid-template-columns: 1fr; }
}

/* Footer */
.np-footer { background: var(--np-fg); color: #fff; padding: 40px 0; }
.np-footer-inner { display: flex; justify-content: space-between; align-items: center; gap: 24px; flex-wrap: wrap; }
.np-footer-brand { font-weight: 700; font-size: 15px; letter-spacing: -0.01em; }
.np-footer-addr { font-size: 13px; color: rgba(255,255,255,0.6); margin-top: 2px; }
.np-footer-link { color: rgba(255,255,255,0.8); text-decoration: none; font-size: 13px; padding: 8px 16px; border: 1px solid rgba(255,255,255,0.2); }
.np-footer-link:hover { color: #fff; border-color: rgba(255,255,255,0.5); }

@media (max-width: 720px) {
    .np-section, .np-hero { padding: 56px 0; }
    .np-section-title { font-size: 26px; }
    .np-headline { font-size: 32px; }
    .np-container, .np-header-inner { padding: 0 20px; }
}
`;
